import { useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/** An action bound to one or more keyboard keys (referenced by key id). */
interface KeyAction {
  description: string;
  keys: string[];
}

const KEY_GROUPS: { group: string; items: KeyAction[] }[] = [
  {
    group: 'Zoom & navigation',
    items: [
      { description: 'Zoom level 1–10', keys: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] },
      { description: 'Zoom in / out', keys: ['equal', 'minus'] },
    ],
  },
  {
    group: 'View',
    items: [
      { description: 'Fullscreen mode', keys: ['f'] },
      { description: 'Side panel show / hide', keys: ['b'] },
      { description: 'Compare mode', keys: ['d'] },
      { description: 'Stacked slides: difference on / off', keys: ['x'] },
      { description: 'Rotate left / right', keys: ['bracketleft', 'bracketright'] },
      { description: 'Guided review: play / pause', keys: ['space'] },
      { description: 'Guided review: pause', keys: ['esc'] },
      { description: 'Guided review: step back / forward', keys: ['arrowleft', 'arrowright'] },
    ],
  },
  {
    group: 'X-ray',
    items: [
      { description: 'Window and level: drag on the image', keys: ['w'] },
      { description: 'Invert', keys: ['i'] },
      { description: 'Stop dragging the window', keys: ['esc'] },
    ],
  },
  {
    group: 'Annotation tools',
    items: [
      { description: 'Arrow', keys: ['a'] },
      { description: 'Measure', keys: ['m'] },
      { description: 'Rectangle', keys: ['r'] },
      { description: 'Ellipse', keys: ['e'] },
      { description: 'Polygon', keys: ['p'] },
      { description: 'Smooth polygon', keys: ['s'] },
      { description: 'Counter', keys: ['c'] },
      { description: 'Finish polygon', keys: ['enter'] },
      { description: 'Remove last polygon point', keys: ['backspace'] },
      { description: 'Cancel polygon in progress', keys: ['esc'] },
      { description: 'Exit tool, finish counting, deselect', keys: ['esc'] },
      { description: 'Remove selected', keys: ['backspace'] },
    ],
  },
];

interface Cap {
  id: string;
  label: string;
  u?: number; // width in key units (default 1)
}

// A compact QWERTY layout. Only ids referenced by KEY_GROUPS are interactive;
// the rest are static filler for a familiar keyboard shape.
const KEY_ROWS: Cap[][] = [
  [
    { id: 'esc', label: 'esc', u: 1.5 },
    { id: '1', label: '1' },
    { id: '2', label: '2' },
    { id: '3', label: '3' },
    { id: '4', label: '4' },
    { id: '5', label: '5' },
    { id: '6', label: '6' },
    { id: '7', label: '7' },
    { id: '8', label: '8' },
    { id: '9', label: '9' },
    { id: '0', label: '0' },
    { id: 'minus', label: '−' },
    { id: 'equal', label: '+' },
    { id: 'backspace', label: '⌫', u: 1.5 },
  ],
  [
    { id: 'tab', label: 'tab', u: 1.5 },
    { id: 'q', label: 'Q' },
    { id: 'w', label: 'W' },
    { id: 'e', label: 'E' },
    { id: 'r', label: 'R' },
    { id: 't', label: 'T' },
    { id: 'y', label: 'Y' },
    { id: 'u', label: 'U' },
    { id: 'i', label: 'I' },
    { id: 'o', label: 'O' },
    { id: 'p', label: 'P' },
    { id: 'bracketleft', label: '[' },
    { id: 'bracketright', label: ']' },
  ],
  [
    { id: 'caps', label: 'caps', u: 1.8 },
    { id: 'a', label: 'A' },
    { id: 's', label: 'S' },
    { id: 'd', label: 'D' },
    { id: 'f', label: 'F' },
    { id: 'g', label: 'G' },
    { id: 'h', label: 'H' },
    { id: 'j', label: 'J' },
    { id: 'k', label: 'K' },
    { id: 'l', label: 'L' },
    { id: 'enter', label: 'return', u: 1.8 },
  ],
  [
    { id: 'shiftl', label: 'shift', u: 2.3 },
    { id: 'z', label: 'Z' },
    { id: 'x', label: 'X' },
    { id: 'c', label: 'C' },
    { id: 'v', label: 'V' },
    { id: 'b', label: 'B' },
    { id: 'n', label: 'N' },
    { id: 'm', label: 'M' },
    { id: 'comma', label: ',' },
    { id: 'period', label: '.' },
    { id: 'shiftr', label: 'shift', u: 2.3 },
  ],
  [
    { id: 'ctrl', label: 'ctrl', u: 1.5 },
    { id: 'opt', label: 'opt', u: 1.3 },
    { id: 'cmd', label: 'cmd', u: 1.6 },
    { id: 'space', label: '', u: 6 },
    { id: 'cmdr', label: 'cmd', u: 1.6 },
    { id: 'optr', label: 'opt', u: 1.3 },
    { id: 'arrowleft', label: '←' },
    { id: 'arrowright', label: '→' },
  ],
];

const KEY_UNIT = 30; // px per key unit

// Every key some action uses; the rest of the keyboard is filler.
const MAPPED = new Set(KEY_GROUPS.flatMap((g) => g.items.flatMap((i) => i.keys)));

/**
 * The viewer's shortcuts reference, opened from the bottom bar of either
 * edition (the enterprise status bar, the community viewer's bar): `children`
 * is that bar's own button.
 *
 * Opened with the mouse, closing it does not hand focus back to the button:
 * a focused button takes Space itself, so the next Space would open this
 * again instead of playing the review scan. Opened from the keyboard, focus
 * goes back as usual.
 *
 * Keys pressed while it is open stay in it: the viewer's own shortcuts listen
 * on the document, and would otherwise zoom, arm a tool or step the scan
 * behind it. Escape still closes it (Radix hears it while capturing).
 */
export function ShortcutsDialog({ children }: { children: ReactElement }) {
  // Keys highlighted by a hovered action; and the key hovered on the keyboard.
  const [active, setActive] = useState<Set<string>>(() => new Set());
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  // A keyboard press of a button clicks with detail 0; a mouse or a tap, 1+.
  const byPointer = useRef(false);

  const isLit = (item: KeyAction) => hoverKey != null && item.keys.includes(hoverKey);

  return (
    <Dialog
      onOpenChange={() => {
        // Closing hands no mouseleave to what was hovered.
        setActive(new Set());
        setHoverKey(null);
      }}
    >
      <DialogTrigger asChild onClick={(e) => (byPointer.current = e.detail > 0)}>
        {children}
      </DialogTrigger>
      <DialogContent
        className="max-w-2xl"
        onKeyDown={(e) => e.stopPropagation()}
        onCloseAutoFocus={(e) => {
          if (byPointer.current) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Shortcuts</DialogTitle>
          <DialogDescription>
            Hover an action to light up its key — or hover a key to find its action.
          </DialogDescription>
        </DialogHeader>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          <div className="space-y-5">
            {KEY_GROUPS.slice(0, 2).map((g) => (
              <Group key={g.group} group={g} isLit={isLit} onEnter={setActive} onLeave={() => setActive(new Set())} />
            ))}
          </div>
          <div>
            {KEY_GROUPS.slice(2).map((g) => (
              <Group key={g.group} group={g} isLit={isLit} onEnter={setActive} onLeave={() => setActive(new Set())} />
            ))}
          </div>
        </div>

        {/* Keyboard */}
        <div className="mt-1">
          <KeyboardGraphic active={active} hoverKey={hoverKey} onHoverKey={setHoverKey} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The keyboard graphic. Keys in `active` (from a hovered action) glow; hovering a
 *  mapped key reports it back via `onHoverKey` so the matching action highlights. */
function KeyboardGraphic({
  active,
  hoverKey,
  onHoverKey,
}: {
  active: Set<string>;
  hoverKey: string | null;
  onHoverKey: (id: string | null) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <div className="mx-auto flex w-fit flex-col gap-1 rounded-lg border bg-muted/40 p-2">
        {KEY_ROWS.map((row, r) => (
          <div key={r} className="flex gap-1">
            {row.map((k) => {
              const isMapped = MAPPED.has(k.id);
              const lit = active.has(k.id) || hoverKey === k.id;
              return (
                <div
                  key={k.id}
                  onMouseEnter={() => isMapped && onHoverKey(k.id)}
                  onMouseLeave={() => isMapped && onHoverKey(null)}
                  className={cn(
                    'flex h-7 select-none items-center justify-center rounded-md border text-[11px] font-medium transition-colors',
                    lit
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : isMapped
                        ? 'border-border bg-background text-foreground'
                        : 'border-border/50 bg-transparent text-muted-foreground/40',
                    isMapped && 'cursor-default',
                  )}
                  style={{ width: (k.u ?? 1) * KEY_UNIT }}
                >
                  {k.label}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupHeading({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

function Group({
  group,
  isLit,
  onEnter,
  onLeave,
}: {
  group: { group: string; items: KeyAction[] };
  isLit: (item: KeyAction) => boolean;
  onEnter: (keys: Set<string>) => void;
  onLeave: () => void;
}) {
  return (
    <div>
      <GroupHeading>{group.group}</GroupHeading>
      <div className="flex flex-col">
        {group.items.map((item) => (
          <div
            key={item.description}
            onMouseEnter={() => onEnter(new Set(item.keys))}
            onMouseLeave={onLeave}
            className={cn(
              'cursor-default rounded px-2 py-1 text-sm transition-colors',
              isLit(item) ? 'bg-primary/15 text-foreground' : 'hover:bg-accent',
            )}
          >
            {item.description}
          </div>
        ))}
      </div>
    </div>
  );
}
